import React, { useEffect, useState } from "react";
import BootstrapTable from "react-bootstrap-table-next";
import paginationFactory from "react-bootstrap-table2-paginator";
import moment from "moment/moment";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { solid } from '@fortawesome/fontawesome-svg-core/import.macro';
import Modal from 'react-bootstrap/Modal';
import { Link } from 'react-router-dom';
import Select from 'react-select';

import { _userChangeStatus, _userHistoryGet, _usersGet } from "../services/apiConnections.service";

export const Users = () => {

    const [token, setToken] = useState(null);
    const [users, setUsers] = useState([]);
    const [error, setError] = useState(null);
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const [totalItems, setTotalItems] = useState(0);
    const [searchEmail, setSearchEmail] = useState('');
    const [showHistory, setShowHistory] = useState(false);
    const [userHistory, setUserHistory] = useState([]);
    const [selectedStatus, setSelectedStatus] = useState({
        label: "All",
        value: -1
    });

    const defaultSorted = [{
        dataField: 'email',
        order: 'asc'
    }];

    const statuses = [
        {
            label: "All",
            value: -1
        },
        {
            label: "Active",
            value: "active"
        },
        {
            label: "Blocked",
            value: "blocked"
        },
        {
            label: "Pending",
            value: "pending"
        },
        {
            label: "Declined",
            value: "declined"
        }
    ];

    useEffect(() => {
        const storedToken = localStorage.getItem('token');
        setToken(storedToken);
    }, []);

    useEffect(() => {
        if (token) {
            getData();
        }
    }, [token]);

    const getData = async (options = {}) => {
        if (!token) return;

        const params = {
            page: options.page !== undefined ? options.page : page,
            page_size: options.pageSize !== undefined ? options.pageSize : pageSize,
            sort_by: options.sortField !== undefined ? options.sortField : defaultSorted[0].dataField,
            sort_order: options.sortOrder !== undefined ? options.sortOrder : defaultSorted[0].order
        };

        const emailToSearch = options.searchEmail !== undefined ? options.searchEmail : searchEmail;
        if (emailToSearch) {
            params.search = emailToSearch;
        }

        const statusToFilter = options.statusValue !== undefined ? options.statusValue : selectedStatus.value;
        if (statusToFilter !== -1) {
            params.state = statusToFilter;
        }

        let result = await _usersGet(token, params);
        if (result.error) {
            console.log("--error:", result.error);
            setError(result.error);
        } else {
            setUsers(result.users);
            setTotalItems(result.total_items);
            // State for page and pageSize is updated in handleTableChange or remains for filters/sort
            if (options.page !== undefined) setPage(options.page);
            if (options.pageSize !== undefined) setPageSize(options.pageSize);
            setError(null);
        }
    };

    const handleTableChange = (type, { page, sizePerPage, sortField, sortOrder }) => {
        switch (type) {
            case 'pagination':
                setPage(page);
                setPageSize(sizePerPage);
                getData({ page, pageSize: sizePerPage, sortField, sortOrder });
                break;
            case 'sort':
                getData({ sortField, sortOrder });
                break;
            default:
                break;
        }
    };

    const handleSearch = (value) => {
        setSearchEmail(value);
        // getData will use the current page and pageSize from state
        getData({ searchEmail: value });
    };

    const handleShowHistory = async (id) => {
        let result = await _userHistoryGet(token, id);
        if (result.error) {
            console.log("--error:", result.error);
        } else {
            for (let s in result.history) {
                if (result.history[s].admin_email) {
                    try {
                        result.history[s].admin_email = JSON.parse(result.history[s].admin_email).user_email;
                    } catch (e) { }
                }
                if (result.history[s].edit_date === null) {
                    result.history[s].edit_date = result.history[s].registration_date;
                }
            }
            setUserHistory(result.history);
        }
        setError(result.error);
    };

    const handleUserChangeStatus = async (user, status) => {
        let result = await _userChangeStatus(token, user, status);
        if (result.error) {
            console.log("--error:", result.error);
        } else {
            getData();
        }
        setError(result.error);
    }

    const dateFormatter = (cell) => {
        if (!cell) {
            return '';
        }
        return moment(new Date(cell)).format('yyyy-MM-DD H:mm');
    }

    const editFormatter = (cell, row) => {
        return (
            <Link className='tableIconBtn' to={`/users/user-edit/${Number(row.id)}`}>
                <FontAwesomeIcon icon={solid('pencil')} />
            </Link>
        )
    };

    const historyFormatter = (cell, row) => {
        return (
            <button
                className="historyBtn"
                onClick={() => {
                    setShowHistory(true);
                    handleShowHistory(row.id);
                }}>Show Logs</button>
        )
    };

    const statusFormatter = (cell) => {
        switch (cell) {
            case 'active':
                return (
                    <span style={{ color: 'green' }}>{cell}</span>
                )
            case 'pending':
                return (
                    <span style={{ color: 'orange' }}>{cell}</span>
                )
            case 'declined':
                return (
                    <span style={{ color: 'red' }}>{cell}</span>
                )
            case 'blocked':
                return (
                    <span style={{ color: 'grey' }}>{cell}</span>
                )
            default:
                return (
                    <span>{cell}</span>
                )
        }
    };

    const actionFormatter = (cell, row) => {
        if (row.status === 'pending' || row.status === 'declined') {
            return (
                <div className="actionBtns">
                    <button
                        className="btn mainBtn approveBtn"
                        onClick={() => handleUserChangeStatus(row, 'active')}>
                        <span>Approve</span>
                        <FontAwesomeIcon icon={solid('check')} />
                    </button>
                    <button
                        className="btn mainBtn declineBtn"
                        disabled={row.status === 'declined'}
                        onClick={() => handleUserChangeStatus(row, 'declined')}>
                        <span>Decline</span>
                        <FontAwesomeIcon icon={solid('xmark')} />
                    </button>
                </div>
            )
        } else if (row.status === 'active') {
            return (
                <span>Approved</span>
            )
        }
    }

    const columns = [
        {
            dataField: 'registration_date',
            text: 'Registration Date',
            formatter: dateFormatter,
            sort: true
        },
        {
            dataField: 'blocked_date',
            text: 'Blocked Date',
            formatter: dateFormatter,
            sort: true
        },
        {
            dataField: 'email',
            text: 'Email',
            sort: true
        },
        {
            dataField: 'first_name',
            text: 'First Name',
            sort: true
        },
        {
            dataField: 'last_name',
            text: 'Last Name',
            sort: true
        },
        {
            dataField: 'balance',
            text: 'Balance',
            sort: true
        },
        {
            dataField: 'id',
            text: 'Edit',
            classes: 'iconColumn',
            formatter: editFormatter
        },
        {
            dataField: 'history',
            text: 'History',
            formatter: historyFormatter
        },
        {
            dataField: 'status',
            text: 'State',
            formatter: statusFormatter
        },
        {
            dataField: 'action',
            text: 'Action',
            formatter: actionFormatter
        }
    ];

    const userHistoryColumns = [
        {
            dataField: 'registration_date',
            text: 'Registration Date',
            formatter: dateFormatter
        },
        {
            dataField: 'blocked_date',
            text: 'Blocked Date',
            formatter: dateFormatter
        },
        {
            dataField: 'edit_date',
            text: 'Edit Date',
            formatter: dateFormatter
        },
        {
            dataField: 'admin_email',
            text: 'Admin',
        },
        {
            dataField: 'status',
            text: 'State',
            formatter: statusFormatter
        }
    ]

    const defaultSortedHistory = [
        {
            dataField: 'edit_date',
            order: 'asc'
        }
    ];

    const pagination = paginationFactory({
        page,
        sizePerPage: pageSize,
        totalSize: totalItems,
        showTotal: true,
        hidePageListOnlyOnePage: true,
        hideSizePerPage: totalItems <= 10
    });

    const noData = () => <p className='emptyTableMessage'>No matching records found</p>;

    return (
        <div className='content wideContent'>
            <div>
                <div className='pageTitle'>
                    <span>Users</span>
                </div>
                {error && <p className='msgBlock errorMsg'>{error}</p>}
                <div className='row'>
                    <div className='col-lg-12'>
                        <div className='myTable'>
                            <div className='tableHeader'>
                                <div className="fewFilters mb-3">
                                    <div className='col-6'>
                                        <label
                                            className='inputLabel'
                                            htmlFor='emailFilter'>
                                            Email
                                        </label>
                                        <input
                                            id='emailFilter'
                                            name='emailFilter'
                                            type='text'
                                            className='searchInput'
                                            placeholder='Search...'
                                            value={searchEmail}
                                            onChange={(event) => handleSearch(event.target.value)}
                                        />
                                    </div>
                                    <div className='col-6'>
                                        <label
                                            className='inputLabel'
                                            htmlFor='statusFilter'>
                                            State
                                        </label>
                                        <Select
                                            id='statusFilter'
                                            name='statusFilter'
                                            className='customSelect'
                                            classNamePrefix='select'
                                            options={statuses}
                                            value={selectedStatus}
                                            onChange={(selectedOption) => {
                                                setSelectedStatus(selectedOption);
                                                getData({ page: 1, statusValue: selectedOption.value });
                                            }} />
                                    </div>
                                </div>
                            </div>
                            <BootstrapTable
                                keyField='id'
                                data={users}
                                columns={columns}
                                wrapperClasses='table-responsive'
                                bordered={false}
                                pagination={pagination}
                                onTableChange={handleTableChange}
                                remote={{ pagination: true, sort: true }}
                                defaultSorted={defaultSorted}
                                noDataIndication={noData} />
                        </div>
                    </div>
                </div>
                <Modal
                    show={showHistory}
                    onHide={() => setShowHistory(false)}
                    backdrop="static"
                    keyboard={false}
                    className="historyModal"
                    centered>
                    <Modal.Header closeButton>
                        <div className='pageTitle'>
                            <span>History</span>
                        </div>
                    </Modal.Header>
                    <Modal.Body>
                        <BootstrapTable
                            keyField='edit_date'
                            data={userHistory}
                            columns={userHistoryColumns}
                            wrapperClasses='table-responsive historyScrollTable'
                            bordered={false}
                            defaultSorted={defaultSortedHistory}
                            noDataIndication={noData} />
                    </Modal.Body>
                </Modal>
            </div>
        </div>
    )
};